import { CheckCircle2, Info, MailCheck, MailWarning, MailX, Send } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import type { Translations } from "@/shared/lang/interfaces/translationTypes";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";

import type { WalletTopUpCreated, WalletTopUpEmailStatus } from "../types/walletTopUps.types";
import { WalletTopUpReceiptActions } from "./WalletTopUpReceiptActions";

const EMAIL_STATUS: Record<
  Exclude<WalletTopUpEmailStatus, "not_configured">,
  { icon: LucideIcon; tone: string; key: keyof Translations["walletTopUps"] }
> = {
  pending: { icon: Send, tone: "text-muted-foreground", key: "email_pending" },
  sent: { icon: MailCheck, tone: "text-success", key: "email_sent" },
  failed: { icon: MailX, tone: "text-destructive", key: "email_failed" },
  no_email: { icon: MailWarning, tone: "text-warning", key: "email_no_address" },
};

/**
 * The receipt as the client will read it, on screen, with what happened to the two emails under it.
 * `recovered`: found by checking a top-up whose answer was lost, so it was credited earlier, not now.
 */
export function WalletTopUpSuccess({
  topUp,
  recovered = false,
}: {
  topUp: WalletTopUpCreated;
  recovered?: boolean;
}) {
  const { t } = useTranslations("walletTopUps");
  const receiptLabel = t(
    topUp.kind === "incident_refund" ? "done_receipt_label_refund" : "done_receipt_label",
  );
  return (
    <div className="space-y-5">
      {recovered ? (
        <ColocaAlert variant="secondary" icon={Info} text={t("done_recovered")} />
      ) : (
        topUp.replayed && <ColocaAlert variant="secondary" icon={Info} text={t("done_replayed")} />
      )}

      <section aria-label={receiptLabel} className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex items-start justify-between gap-4 bg-primary/5 px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-medium tracking-[0.12em] text-muted-foreground uppercase">
              {receiptLabel}
            </p>
            <p className="mt-0.5 font-mono text-sm font-semibold text-foreground">{topUp.reference}</p>
          </div>
          <ColocaStatusPill variant="success" icon={CheckCircle2}>
            {t("done_credited")}
          </ColocaStatusPill>
        </div>
        <div className="px-5 pt-4 pb-5">
          <p className="text-xs text-muted-foreground">{t("done_amount")}</p>
          <div className="mt-1 text-primary">
            <ColocaAmountDisplay
              value={topUp.amount}
              currency={topUp.currency}
              fractionDigits={minorUnitsFor(topUp.currency)}
              size="xl"
            />
          </div>
          <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <ReceiptField label={t("done_beneficiary")} value={topUp.organization_name ?? "—"} />
            <ReceiptField
              label={t("done_wallet")}
              value={t("review_wallet_value", { currency: topUp.currency })}
            />
            <ReceiptField label={t("done_concept")} value={topUp.concept} />
            <ReceiptField label={t("done_origin")} value={topUp.origin} />
          </dl>
        </div>
      </section>

      {topUp.emails && (
        <ul className="space-y-2" aria-label={t("done_emails_label")}>
          <EmailLine status={topUp.emails.operator} who={t("email_to_operator")} />
          <EmailLine status={topUp.emails.organization} who={t("email_to_organization")} />
          <EmailLine status={topUp.emails.accounting} who={t("email_to_accounting")} />
        </ul>
      )}

      <WalletTopUpReceiptActions topUp={topUp} size="default" />
    </div>
  );
}

function ReceiptField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words font-medium text-foreground">{value}</dd>
    </div>
  );
}

function EmailLine({ status, who }: { status: WalletTopUpEmailStatus; who: string }) {
  const { t } = useTranslations("walletTopUps");
  if (status === "not_configured") return null;
  // A status this build does not know yet reads as on its way rather than breaking the receipt.
  const { icon: Icon, tone, key } = EMAIL_STATUS[status] ?? EMAIL_STATUS.pending;
  return (
    <li className="flex items-start gap-2 text-sm text-foreground">
      <Icon className={cn("mt-0.5 size-4 shrink-0", tone)} aria-hidden="true" />
      <span>{t(key, { who })}</span>
    </li>
  );
}
